import { useMemo } from 'react';

import useTreadAgentConversationQuery from '~queries/useTreadAgentConversationQuery';
import useTreadAgentMessagesInfiniteQuery from '~queries/useTreadAgentMessagesInfiniteQuery';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';

/**
 * Unifies the two state sources for a single conversation into one view model:
 * durable history from React Query and the live streaming turn from TreadAgentStore. Both
 * the floating panel and the full page render from this so they can never drift.
 *
 * The live turn is reconciled with durable history purely by derivation (no effects): once
 * the refetched history includes the turn's final assistant message, `liveTurn` becomes null
 * so the persisted row takes over. There is therefore never a frame where both the live turn
 * and its persisted copy render (the old end-of-turn duplicate), and nothing has to be
 * cleared in the store after the fact.
 */
export function useConversationView(
  companyId: Nullable<string>,
  conversationId: Nullable<string>,
) {
  const { treadAgentStore } = useStores();
  const { conversation, isFetching } = useTreadAgentConversationQuery(
    companyId,
    conversationId,
  );
  const {
    messages: persistedMessages,
    isLoading: isLoadingMessages,
    hasOlder,
    isLoadingOlder,
    fetchOlder,
  } = useTreadAgentMessagesInfiniteQuery(companyId, conversationId);

  const activeTurn = treadAgentStore.activeTurn;

  // The live turn to render, or null once its final assistant message is persisted — the
  // persisted row renders it instead. Null finalMessageId means the turn hasn't completed, so
  // keep showing it; a later turn_started resets it.
  const liveTurn = useMemo(() => {
    const supersededByPersisted =
      activeTurn.finalMessageId !== null &&
      persistedMessages.some(({ id }) => id === activeTurn.finalMessageId);

    return supersededByPersisted ? null : activeTurn;
  }, [activeTurn, persistedMessages]);

  // Still loading while the conversation fetch hasn't produced data, or the transcript's
  // first page is in flight.
  const isLoadingConversation = (isFetching && !conversation) || isLoadingMessages;

  return {
    fetchOlder,
    hasOlder,
    isLoadingConversation,
    isLoadingOlder,
    liveTurn,
    messages: persistedMessages,
  };
}
